// ink-component
/* eslint-disable react/jsx-boolean-value */
import * as React from 'react';
import styled from 'styled-components';

/* @internal */
const StyledVirtualWrapper = styled.div<{
  $height?: React.CSSProperties['height'];
  $overflow?: React.CSSProperties['overflow'];
}>`
  position: relative;
  overflow: ${({ $overflow }) => $overflow};
  height: ${({ $height }) => $height};
`;

export type NewTableVirtualContextType = null | {
  section: 'wrapper' | 'head' | 'body';
  type: 'window' | 'container';
};

/** Utility interface to expand HTML entity types to accept `virtual` as a prop */
export interface NewTableVirtualizedElement {
  virtual?: boolean;
}

export const NewTableVirtualContext = React.createContext<NewTableVirtualContextType>(null);

export interface NewTableVirtualWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Defines the height of virtualization scrolling wrapper. Use with a CSS length unit. */
  height?: React.CSSProperties['height'];
  /** @ignore */
  type?: 'window' | 'container';
}

const NewTableVirtualWrapper = React.forwardRef<HTMLDivElement, NewTableVirtualWrapperProps>(
  ({ height, type, ...props }, ref) => {
    if (height !== undefined) {
      throw new Error(
        `NewTable.VirtualWrapper: A height is not required anymore! Move the height prop back to \`NewTable\`.`,
      );
    }

    if (!ref) {
      throw new Error(
        'NewTable.VirtualWrapper: Virtualization requires spreading ...virtualizer.getWrapperProps() to NewTable.VirtualWrapper.',
      );
    }

    const context = React.useMemo(
      () =>
        ({
          section: 'wrapper',
          type: type || 'container',
        } as const),
      [type],
    );

    return (
      <NewTableVirtualContext.Provider value={context}>
        <StyledVirtualWrapper
          ref={ref}
          $height={type === 'container' ? height : undefined}
          $overflow={type === 'container' ? 'auto' : 'visible'}
          {...props}
        />
      </NewTableVirtualContext.Provider>
    );
  },
);

NewTableVirtualWrapper.displayName = 'NewTable.VirtualWrapper';

export default NewTableVirtualWrapper;
